import { FormEvent, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '../api/client';
import { OrgLayout } from '../components/OrgLayout';
import { RecordInfoButton } from '../components/RecordInfoButton';
import { Modal } from '../components/Modal';
import { useToast } from '../components/Toast';
import type { AcademicYear } from '../types/models';

const emptyForm = { code: '', name: '', startDate: '', endDate: '' };

// "2024-06-01" -> "01-06-2024" for display.
function fmtDate(iso: string) {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-');
  return d && m && y ? `${d}-${m}-${y}` : iso;
}

export function AcademicYearPage() {
  const { orgId = '' } = useParams();
  const toast = useToast();

  const [years, setYears] = useState<AcademicYear[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  async function refresh() {
    try {
      setYears(await api.listAcademicYears(orgId));
    } catch (e: any) {
      setError(e?.message ?? 'Failed to load academic years.');
    }
  }

  const filtered = useMemo(() => {
    if (!years) return null;
    const q = search.trim().toLowerCase();
    if (!q) return years;
    return years.filter(y => y.code.toLowerCase().includes(q) || y.name.toLowerCase().includes(q));
  }, [years, search]);

  function openCreate() {
    setFormError(null); setEditId(null); setForm({ ...emptyForm }); setShowModal(true);
  }

  function openEdit(y: AcademicYear) {
    setFormError(null);
    setEditId(y.id);
    setForm({ code: y.code, name: y.name, startDate: y.startDate, endDate: y.endDate });
    setShowModal(true);
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setFormError(null);
    if (form.endDate <= form.startDate) {
      setFormError('End date must be after the start date.');
      return;
    }
    setBusy(true);
    const payload = {
      code: form.code.trim(), name: form.name.trim(),
      startDate: form.startDate, endDate: form.endDate,
    };
    try {
      if (editId) await api.updateAcademicYear(editId, payload);
      else await api.createAcademicYear({ organizationId: orgId, ...payload });
      setShowModal(false);
      await refresh();
      toast.success(editId ? 'Academic year updated.' : 'Academic year created.');
    } catch (e: any) {
      setFormError(e?.message ?? 'Failed to save academic year.');
    } finally {
      setBusy(false);
    }
  }

  async function remove(y: AcademicYear) {
    if (!confirm(`Delete academic year “${y.name}”? This cannot be undone.`)) return;
    setError(null);
    try {
      await api.deleteAcademicYear(y.id);
      await refresh();
      toast.success('Academic year deleted.');
    } catch (e: any) {
      const msg = e?.message ?? 'Failed to delete academic year.';
      setError(msg);
      toast.error(msg);
    }
  }

  return (
    <OrgLayout>
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <h1 style={{ margin: 0 }}>Academic Year</h1>
        <button className="primary" onClick={openCreate}>+ Add academic year</button>
      </div>

      <div className="card" style={{ marginTop: 16 }}>
        <label style={{ flex: 1 }}>
          <div className="muted">Code or Name</div>
          <input value={search} placeholder="Code / Name" onChange={e => setSearch(e.target.value)} />
        </label>
      </div>

      {error && <div className="error" style={{ margin: '12px 0' }}>{error}</div>}

      <div className="card" style={{ marginTop: 16, padding: 0, overflowX: 'auto' }}>
        <table className="data-table">
          <thead>
            <tr>
              <th style={{ width: 56 }}>S.no</th>
              <th>Academic Year Code</th>
              <th>Academic Year Name</th>
              <th>Start Date</th>
              <th>End Date</th>
              <th style={{ width: 180 }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {!filtered && <tr><td colSpan={6} className="muted">Loading…</td></tr>}
            {filtered && filtered.length === 0 && (
              <tr><td colSpan={6} className="muted">
                {search ? 'No academic years match your search.' : 'No academic years yet.'}
              </td></tr>
            )}
            {filtered?.map((y, idx) => (
              <tr key={y.id}>
                <td>{idx + 1}</td>
                <td className="slug">{y.code}</td>
                <td style={{ fontWeight: 600 }}>{y.name}</td>
                <td>{fmtDate(y.startDate)}</td>
                <td>{fmtDate(y.endDate)}</td>
                <td>
                  <button className="link-btn" onClick={() => openEdit(y)}>Edit</button>
                  <button className="link-btn" style={{ color: 'var(--danger)' }} onClick={() => remove(y)}>Delete</button>
                  <RecordInfoButton entity="academic-years" id={y.id} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showModal && (
        <Modal title={editId ? 'Edit academic year' : 'Create academic year'} onClose={() => setShowModal(false)}>
          <form onSubmit={submit} className="col" style={{ marginTop: 16 }}>
            <div className="row" style={{ gap: 12 }}>
              <label style={{ flex: '0 0 180px' }}>
                <div className="muted">Academic Year Code *</div>
                <input value={form.code} required maxLength={32} autoFocus
                       onChange={e => setForm({ ...form, code: e.target.value })} placeholder="2024-2025" />
              </label>
              <label style={{ flex: 1 }}>
                <div className="muted">Academic Year Name *</div>
                <input value={form.name} required maxLength={100}
                       onChange={e => setForm({ ...form, name: e.target.value })} placeholder="2024-2025" />
              </label>
            </div>
            <div className="row" style={{ gap: 12 }}>
              <label style={{ flex: 1 }}>
                <div className="muted">Start Date *</div>
                <input type="date" value={form.startDate} required
                       onChange={e => setForm({ ...form, startDate: e.target.value })} />
              </label>
              <label style={{ flex: 1 }}>
                <div className="muted">End Date *</div>
                <input type="date" value={form.endDate} required
                       onChange={e => setForm({ ...form, endDate: e.target.value })} />
              </label>
            </div>
            {formError && <div className="error">{formError}</div>}
            <div className="modal-actions">
              <button type="button" onClick={() => setShowModal(false)}>Cancel</button>
              <button type="submit" className="primary" disabled={busy}>
                {busy ? 'Saving…' : editId ? 'Save changes' : 'Create academic year'}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </OrgLayout>
  );
}
